/**
 * Dashboard Layout — application shell.
 *
 * Fixed-height frame: sidebar + header + independently scrolling <main> +
 * slim status bar. New pages render inside <main> and should not introduce a
 * second scroll container.
 */

import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, Search, Settings, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { locateRoute } from '@/layouts/nav';
import { Sidebar } from '@/components/shell/sidebar';
import { QuickNav } from '@/components/shell/quick-nav';
import { MarketStatus } from '@/components/shell/market-status';
import { IndexStrip } from '@/components/shell/index-strip';
import { Dropdown, MenuItem } from '@/components/ui/dropdown';
import { EmptyState } from '@/components/ui';
import { cn } from '@/lib/cn';

const COLLAPSE_KEY = 'ui:sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, fetchProfile } = useAuthStore();

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickNavOpen, setQuickNavOpen] = useState(false);

  const { section, item } = locateRoute(location.pathname);

  useEffect(() => {
    if (!user) fetchProfile();
  }, [user, fetchProfile]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage unavailable — collapse state is a convenience, not state we own */
    }
  }, [collapsed]);

  // Global shortcuts: ⌘K / Ctrl+K anywhere, "/" when not typing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setQuickNavOpen(true);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        setQuickNavOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initial = user?.username?.charAt(0).toUpperCase() ?? 'U';

  return (
    <div className="flex h-screen w-full overflow-hidden bg-canvas">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:rounded-lg focus:bg-surface-3 focus:px-3 focus:py-2 focus:text-sm focus:text-fg"
      >
        Skip to content
      </a>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        pathname={location.pathname}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            aria-controls="app-sidebar"
            aria-expanded={mobileOpen}
            className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>

          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex items-center gap-1.5 text-sm">
              {section && (
                <>
                  <li className="hidden text-fg-subtle sm:block">{section}</li>
                  <li aria-hidden="true" className="hidden sm:block">
                    <ChevronRight className="h-3.5 w-3.5 text-fg-faint" />
                  </li>
                </>
              )}
              <li className="truncate font-medium text-fg" aria-current="page">
                {item?.label ?? 'Dashboard'}
              </li>
            </ol>
          </nav>

          <div className="flex flex-1 items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setQuickNavOpen(true)}
              className="hidden h-8 items-center gap-2 rounded-lg border border-line-strong bg-surface-2 pr-1.5 pl-2.5 text-xs text-fg-subtle transition-colors hover:border-line-hover hover:text-fg-muted md:flex"
            >
              <Search className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Go to…</span>
              <kbd className="rounded border border-line-strong bg-surface-3 px-1 font-tabular text-[10px] text-fg-faint">
                ⌘K
              </kbd>
            </button>

            <button
              type="button"
              onClick={() => setQuickNavOpen(true)}
              aria-label="Go to screen"
              className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg md:hidden"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
            </button>

            <MarketStatus className="hidden lg:block" />

            <Dropdown
              renderTrigger={({ open, triggerProps }) => (
                <button
                  aria-label="Notifications"
                  className={cn(
                    'rounded-md p-1.5 transition-colors hover:bg-surface-2 hover:text-fg',
                    open ? 'bg-surface-2 text-fg' : 'text-fg-muted'
                  )}
                  {...triggerProps}
                >
                  <Bell className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              panelClassName="w-72 p-0"
            >
              {() => (
                <EmptyState
                  icon={Bell}
                  title="No notifications"
                  description="Order fills and risk events will appear here once notifications are wired up."
                  className="py-8"
                />
              )}
            </Dropdown>

            <Dropdown
              renderTrigger={({ open, triggerProps }) => (
                <button
                  className={cn(
                    'flex h-8 items-center gap-2 rounded-lg border pr-2 pl-1 transition-colors',
                    open ? 'border-line-strong bg-surface-2' : 'border-transparent hover:bg-surface-2'
                  )}
                  {...triggerProps}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand text-xs font-semibold text-white"
                    aria-hidden="true"
                  >
                    {initial}
                  </span>
                  <span className="hidden text-xs font-medium text-fg-muted sm:block">
                    {user?.username ?? 'Account'}
                  </span>
                </button>
              )}
            >
              {(close) => (
                <>
                  <div className="border-b border-line px-2.5 pt-1.5 pb-2">
                    <p className="truncate text-sm font-medium text-fg">{user?.username ?? 'Account'}</p>
                    <p className="truncate text-xs text-fg-subtle">{user?.email ?? '—'}</p>
                    {user?.role && (
                      <p className="mt-1 font-tabular text-[10px] tracking-wider text-fg-faint uppercase">
                        {user.role}
                      </p>
                    )}
                  </div>
                  <div className="pt-1">
                    <MenuItem
                      icon={Settings}
                      onClick={() => {
                        close();
                        navigate('/settings');
                      }}
                    >
                      Settings
                    </MenuItem>
                    <MenuItem icon={LogOut} danger onClick={handleLogout}>
                      Sign out
                    </MenuItem>
                  </div>
                </>
              )}
            </Dropdown>
          </div>
        </header>

        {/* Index strip — NIFTY / BANKNIFTY / … configurable per user */}
        <IndexStrip />

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 lg:px-6 lg:py-8">
            <Outlet />
          </div>
        </main>

        {/* Status bar */}
        <footer className="flex h-8 shrink-0 items-center justify-between gap-4 border-t border-line bg-canvas px-4 text-[11px] text-fg-faint lg:px-6">
          <div className="flex items-center gap-4">
            <MarketStatus className="lg:hidden" />
            <span className="hidden sm:inline">
              Broker: <span className="text-fg-subtle">Kotak Neo</span>
            </span>
          </div>
          <div className="flex items-center gap-4 font-tabular">
            <span>v1.0.0</span>
            <span className="hidden sm:inline">© 2026 TradePlatform</span>
          </div>
        </footer>
      </div>

      {quickNavOpen && <QuickNav open onClose={() => setQuickNavOpen(false)} />}
    </div>
  );
}
